import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import ChatArea from './components/ChatArea';
import DashboardTabs from './components/DashboardTabs';
import CollectionsPanel from './components/CollectionsPanel';
import InventoryPanel from './components/InventoryPanel';
import SalesPanel from './components/SalesPanel';
import PerformancePanel from './components/PerformancePanel';
import IntroScreen from './components/IntroScreen';
import { useStats } from './hooks/useStats';

function App() {
  const [activeTab, setActiveTab] = useState('chat');

  // Mobile sidebar drawer state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Intro splash screen — show once per browser session
  const [showIntro, setShowIntro] = useState(() => {
    if (sessionStorage.getItem('introShown')) return false;
    sessionStorage.setItem('introShown', '1');
    return true;
  });

  // Theme — light (Professional) ↔ dark. Persisted in localStorage.
  const [theme, setTheme] = useState(() =>
    localStorage.getItem('theme') || 'light'
  );
  const toggleTheme = () =>
    setTheme(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', next);
      return next;
    });

  // RAG toggle — persisted in localStorage
  const [ragEnabled, setRagEnabled] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ragEnabled')) ?? true; }
    catch { return true; }
  });

  // Real stats — queries today, avg latency (no hardcoded numbers)
  const { stats, recordQuery } = useStats();

  // ── Conversations — persisted in localStorage ──────────────────────────────
  const [conversations, setConversations] = useState(() => {
    try {
      const saved = localStorage.getItem('conversations');
      if (saved) return JSON.parse(saved);
    } catch {}
    return [{ id: Date.now(), title: 'New conversation', messages: [] }];
  });

  const [activeChatId, setActiveChatId] = useState(() => {
    try {
      const savedId = JSON.parse(localStorage.getItem('activeChatId'));
      const savedConvs = JSON.parse(localStorage.getItem('conversations'));
      if (savedConvs && savedId && savedConvs.find(c => c.id === savedId)) {
        return savedId;
      }
      return savedConvs?.[0]?.id ?? Date.now();
    } catch {
      return Date.now();
    }
  });

  // ── Persist to localStorage on every change ────────────────────────────────
  useEffect(() => {
    localStorage.setItem('conversations', JSON.stringify(conversations));
  }, [conversations]);

  useEffect(() => {
    localStorage.setItem('activeChatId', JSON.stringify(activeChatId));
  }, [activeChatId]);

  useEffect(() => {
    localStorage.setItem('ragEnabled', JSON.stringify(ragEnabled));
  }, [ragEnabled]);

  // Close sidebar on tab change (mobile UX)
  useEffect(() => {
    setSidebarOpen(false);
  }, [activeTab, activeChatId]);

  // ── Active chat helpers ────────────────────────────────────────────────────
  const activeConversation = conversations.find(c => c.id === activeChatId);
  const messages = activeConversation?.messages ?? [];

  const setMessages = (updater) => {
    setConversations(prev => prev.map(c => {
      if (c.id !== activeChatId) return c;
      const newMessages = typeof updater === 'function' ? updater(c.messages) : updater;
      const firstUserMsg = newMessages.find(m => m.role === 'user');
      const title = firstUserMsg
        ? firstUserMsg.content.slice(0, 35) + (firstUserMsg.content.length > 35 ? '…' : '')
        : c.title;
      return { ...c, messages: newMessages, title };
    }));
  };

  const newConversation = () => {
    const newId = Date.now();
    setConversations(prev => [
      { id: newId, title: 'New conversation', messages: [] },
      ...prev
    ]);
    setActiveChatId(newId);
  };

  const deleteConversation = (id) => {
    setConversations(prev => {
      const remaining = prev.filter(c => c.id !== id);
      if (id === activeChatId) {
        if (remaining.length > 0) {
          setActiveChatId(remaining[0].id);
        } else {
          const newId = Date.now();
          setActiveChatId(newId);
          return [{ id: newId, title: 'New conversation', messages: [] }];
        }
      }
      return remaining;
    });
  };

  return (
    <div className="flex h-full overflow-hidden page-bg relative" data-theme={theme}>

      {/* ── Intro splash screen — shows once per session ── */}
      {showIntro && (
        <IntroScreen
          theme={theme}
          onComplete={() => setShowIntro(false)}
        />
      )}

      {/* ── Floating ambient orbs (purely decorative, pointer-events:none) ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="orb-1 absolute top-[-10%] left-[10%]  w-80 h-80 rounded-full bg-indigo-300/30 blur-3xl" />
        <div className="orb-2 absolute bottom-[-5%] right-[15%] w-96 h-96 rounded-full bg-violet-300/20 blur-3xl" />
        <div className="orb-3 absolute top-[40%]  left-[55%]  w-64 h-64 rounded-full bg-emerald-200/20 blur-3xl" />
      </div>

      {/* ── Mobile overlay backdrop — tap outside sidebar to close ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden backdrop-blur-[2px]"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar — fixed on mobile (drawer), fixed on desktop (always visible) ── */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        ragEnabled={ragEnabled}
        setRagEnabled={setRagEnabled}
        stats={stats}
        conversations={conversations}
        activeChatId={activeChatId}
        setActiveChatId={setActiveChatId}
        newConversation={newConversation}
        deleteConversation={deleteConversation}
        theme={theme}
        toggleTheme={toggleTheme}
      />

      {/* ── Main content — on desktop offset by sidebar width ── */}
      <div className="relative z-10 flex-1 flex flex-col overflow-hidden md:ml-72">
        <DashboardTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onMenuClick={() => setSidebarOpen(prev => !prev)}
        />

        {/* key forces remount → triggers tab-panel fade animation on every switch */}
        <div key={activeTab} className="tab-panel flex-1 flex flex-col overflow-hidden">
          {activeTab === 'chat' && (
            <ChatArea
              key={activeChatId}
              ragEnabled={ragEnabled}
              messages={messages}
              setMessages={setMessages}
              onQueryComplete={recordQuery}
              theme={theme}
              newConversation={newConversation}
            />
          )}
          {activeTab === 'collections' && <CollectionsPanel />}
          {activeTab === 'inventory'   && <InventoryPanel />}
          {activeTab === 'sales'       && <SalesPanel />}
          {activeTab === 'performance' && <PerformancePanel stats={stats} />}
        </div>
      </div>
    </div>
  );

}

export default App;
